import { ProberRegion } from './prober-region';
import {
  GameProbeConfiguration,
  GameProbeResult,
  GameServerChange,
} from './probes/game';
import { HTTPProbeConfiguration, HTTPProbeResult } from './probes/http';
import { ICMPProbeConfiguration, ICMPProbeResult } from './probes/icmp';
import { Service } from './service';
import { Statistic } from './statistics';

// Define the probe types
export type ProbeType = 'ICMP' | 'HTTP' | 'GAME';

export type AvailabilityPolicy = 'ANY_UP' | 'ALL_UP' | 'MAJORITY';

export type RegionStatus = 'UNKNOWN' | 'UP' | 'DOWN';

export type MonitorFailureVerdict = 'UNKNOWN' | 'TARGET_SIDE' | 'REGIONAL';

// Base interface for common monitor properties
interface BaseMonitor {
  id: string;
  name: string;
  service?: Service;
  scheduling: SchedulingConfiguration;
  createdAt: Date;
  updatedAt: Date;
  probeType: ProbeType;
  statistics?: MonitorStatistics;
  paused: boolean;
  regionScopes: string[];
  availabilityPolicy: AvailabilityPolicy | null;
  /** Slim live status per region — populated by the monitors LIST query
   *  (the detail page fetches full RegionHealth separately). */
  regionHealth?: SlimRegionHealth[];
}

/** Slim per-region health for the monitors LIST query: the status plus the
 *  last probe's error category, which feeds the card's outage summary line
 *  ("request timeout") without pulling the full RegionHealth payload. */
export interface SlimRegionHealth {
  status: RegionStatus;
  lastProbe?: Pick<MiniProbe, 'errorCategory'> | null;
}

export interface MonitorStatistics {
  windowStart: Date;
  windowEnd: Date;
  availability: MonitorAvailabilityStatistics;
  availabilityTimeline?: MonitorAvailabilityTimelineEntry[];
  probeMetrics?: ProbeMetrics;
  failures?: MonitorFailureList;
  regions?: RegionStatistics[];
  /** Version and map changes; game monitors only. */
  gameServerChanges?: GameServerChange[];
}

export interface MonitorAvailabilityStatistics {
  uptimeSeconds: number;
  downtimeSeconds: number;
  availabilityPercentage: number;
  totalMonitoredSeconds: number;
  totalWindowSeconds: number;
  regionsReporting: number;
  regionsConfigured: number;
  appliedPolicy?: AvailabilityPolicy | null;
}

export interface ProbeMetrics {
  metrics: Statistic[];
}

export interface RegionStatistics {
  region: ProberRegion;
  availability: MonitorAvailabilityStatistics;
  availabilityTimeline: MonitorAvailabilityTimelineEntry[];
  probeMetrics: ProbeMetrics;
}

export interface MonitorFailureList {
  paginationMetadata: { totalCount: number };
  failures: MonitorFailure[];
}

export interface MiniProbe {
  id: string;
  timestamp: Date;
  succeeded: boolean;
  durationMs: number;
  errorCategory?: string;
}

export interface RegionHealth {
  region: ProberRegion;
  status: RegionStatus;
  lastProbe?: MiniProbe;
}

/** Live probe-result event from the `liveProbeResults` subscription.
 *  `result` is null when the probe did not succeed — read the union fields
 *  only when `metadata.succeeded` is true. */
export interface LiveProbeResult {
  metadata: {
    id: string;
    timestamp: Date;
    succeeded: boolean;
    error?: string | null;
    proberRegionSlug: string;
    attempts: number;
    durationMs: number;
  };
  result?:
    | { __typename: 'HTTPProbeResult'; durationMs: number; statusCode: number }
    | {
        __typename: 'ICMPProbeResult';
        avgRttMs: number;
        packetLossPercent: number;
      }
    | ({ __typename: 'GameProbeResult' } & GameProbeResult)
    | null;
}

export interface MonitorFailure {
  id: string;
  start: Date;
  end?: Date;
  affectedRegions: ProberRegion[];
  policyTriggered: boolean;
  verdict: MonitorFailureVerdict;
  probeFailureIds: string[];
  /** Id of the alert the availability policy emitted for this failure
   *  group; null when no alert was triggered. */
  alertId?: string | null;
  /** Underlying probe failures, populated client-side by the chart data
   *  provider so the failure tooltip can show concrete reasons
   *  (timeout, invalid status code, …) per region. Not part of the
   *  GraphQL fragment — derived from a parallel `getProbeFailures`
   *  fetch. Optional because not every caller enriches the failure. */
  probeFailures?: ProbeFailure[];
}

export interface MonitorAvailabilityTimelineEntry {
  timestamp: Date;
  entry: MonitorAvailabilityStatistics;
  unknown: boolean;
}

export interface SchedulingConfiguration {
  intervalSeconds: number;
  retries: number;
}

// Specific Monitor interfaces based on probe type
export interface ICMPMonitor extends BaseMonitor {
  probeType: 'ICMP';
  probeConfiguration: ICMPProbeConfiguration;
  probeResults: ProbeResult<ICMPProbeResult>[];
}

export interface HTTPMonitor extends BaseMonitor {
  probeType: 'HTTP';
  probeConfiguration: HTTPProbeConfiguration;
  probeResults: ProbeResult<HTTPProbeResult>[];
}

export interface GameMonitor extends BaseMonitor {
  probeType: 'GAME';
  probeConfiguration: GameProbeConfiguration;
  probeResults: ProbeResult<GameProbeResult>[];
}

// Union type that includes all possible Monitor types
export type Monitor = ICMPMonitor | HTTPMonitor | GameMonitor;

// Base interface for common probe result properties
export interface ProbeResult<T> {
  metadata: ProbeResultMetadata;
  result?: T;
}

export interface ProbeResultMetadata {
  id: string;
  timestamp: Date;
  succeeded: boolean;
  error?: string;
  proberRegionSlug?: string;
}

export interface MonitorSpec {
  name: string;
  publicServiceId: number;
  scheduling: SchedulingConfiguration;
  probeType: ProbeType;
  paused: boolean;
  icmpProbeConfiguration?: ICMPProbeConfiguration;
  httpProbeConfiguration?: HTTPProbeConfiguration;
  gameProbeConfiguration?: GameProbeConfiguration;
  proberRegionScopes?: string[];
  availabilityPolicy?: AvailabilityPolicy | null;
}

export interface ProbeFailureGroup {
  failureStart: Date;
  failureEnd: Date;
  failureCategory: string;
  failureDurationMs: number;
}

export interface ProbeFailure {
  id: string;
  start: Date;
  end?: Date;
  category: string;
  details: string;
  probeConfiguration: ProbeConfiguration;
  proberRegionSlug?: string;
}

/**
 * A monitor failure whose alert is related to an incident, with the monitor
 * it belongs to and the per-region probe failure that opened it.
 */
export interface MonitorOutage {
  monitor: Monitor;
  failure: MonitorFailure;
  /** Earliest per-region probe failure of the outage. */
  triggeringFailure?: Pick<
    ProbeFailure,
    'category' | 'details' | 'proberRegionSlug' | 'start'
  > | null;
}

export interface ProbeConfiguration {
  id: string;
  validFrom: Date;
  validTo?: Date;
  configuration:
    | ICMPProbeConfiguration
    | HTTPProbeConfiguration
    | GameProbeConfiguration;
}

export interface ProbeMetricsOptions {
  percentile: number;
}

export const resolveRegionSettings = (
  monitor: Monitor,
  spec: Partial<MonitorSpec>
): Pick<MonitorSpec, 'proberRegionScopes' | 'availabilityPolicy'> => {
  const proberRegionScopes =
    spec.proberRegionScopes ?? monitor.regionScopes ?? undefined;
  const availabilityPolicy =
    spec.availabilityPolicy !== undefined
      ? spec.availabilityPolicy
      : monitor.availabilityPolicy;
  return { proberRegionScopes, availabilityPolicy };
};

/** The spec field that carries each probe type's configuration. */
const PROBE_CONFIGURATION_FIELDS = {
  HTTP: 'httpProbeConfiguration',
  ICMP: 'icmpProbeConfiguration',
  GAME: 'gameProbeConfiguration',
} as const satisfies Record<ProbeType, keyof MonitorSpec>;

type ProbeSettings = Pick<
  MonitorSpec,
  | 'probeType'
  | 'httpProbeConfiguration'
  | 'icmpProbeConfiguration'
  | 'gameProbeConfiguration'
>;

/**
 * The probe type and its configuration for an update: the spec's override,
 * else the monitor's current configuration. Only the active type's field is
 * set, so the API never receives a stale config of another type.
 */
export const resolveProbeSettings = (
  monitor: Monitor,
  spec: Partial<MonitorSpec>
): ProbeSettings => {
  const probeType: ProbeType = spec.probeType ?? monitor.probeType;
  const field = PROBE_CONFIGURATION_FIELDS[probeType];
  const configuration =
    spec[field] ??
    (probeType === monitor.probeType ? monitor.probeConfiguration : undefined);

  if (probeType !== monitor.probeType && !spec[field]) {
    throw new Error(`Switching probeType to ${probeType} requires ${field}.`);
  }

  return {
    probeType,
    httpProbeConfiguration: undefined,
    icmpProbeConfiguration: undefined,
    gameProbeConfiguration: undefined,
    [field]: configuration,
  };
};
